import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import TicketsManager from "../../components/TicketsManagerTabs/";
import Ticket from "../../components/Ticket/";
import { i18n } from "../../translate/i18n";

const STORAGE_KEY = "watoolx_tickets_list_width_px";

function clampListWidth(px) {
	const min = 200;
	const max = Math.max(min + 80, Math.floor(window.innerWidth * 0.78));
	return Math.min(Math.max(px, min), max);
}

function readInitialWidth() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const n = raw ? parseInt(raw, 10) : NaN;
		if (Number.isFinite(n)) return clampListWidth(n);
	} catch {
		/* noop */
	}
	return clampListWidth(Math.round(window.innerWidth * 0.25));
}

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: theme.spacing(0),
		height: `calc(100% - 48px)`,
		overflowY: "hidden",
	},

	chatPapper: {
		display: "flex",
		height: "100%",
	},

	splitRoot: {
		display: "flex",
		flexDirection: "row",
		height: "100%",
		width: "100%",
		minHeight: 0,
		overflow: "hidden",
	},

	listPane: {
		flex: "none",
		display: "flex",
		flexDirection: "column",
		height: "100%",
		minWidth: 200,
		overflow: "hidden",
	},

	resizer: {
		flex: "none",
		width: 6,
		marginLeft: -1,
		cursor: "col-resize",
		backgroundColor:
			theme.palette.type === "dark" || theme.mode === "dark" ? "#4b5563" : "#e0e0e0",
		borderLeft: `1px solid ${
			theme.palette.type === "dark" || theme.mode === "dark" ? "#374151" : "#d1d5db"
		}`,
		borderRight: `1px solid ${
			theme.palette.type === "dark" || theme.mode === "dark" ? "#374151" : "#d1d5db"
		}`,
		zIndex: 2,
		touchAction: "none",
		"&:hover": {
			backgroundColor:
				theme.palette.type === "dark" ? theme.palette.primary.dark : theme.palette.primary.light,
		},
	},

	messagesWrapper: {
		flex: 1,
		display: "flex",
		height: "100%",
		flexDirection: "column",
		minWidth: 240,
		overflow: "hidden",
	},
	welcomeMsg: {
		backgroundColor: theme.palette.boxticket,
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
		padding: theme.spacing(3),
	},
	logoContainer: {
		marginBottom: theme.spacing(3),
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	logoImage: {
		maxWidth: "450px",
		maxHeight: "450px",
		width: "auto",
		height: "auto",
		objectFit: "contain",
		imageRendering: "auto",
	},
	welcomeText: {
		marginBottom: theme.spacing(1),
		textAlign: "center",
		fontSize: "1.5rem",
	},
	subtitleText: {
		marginBottom: theme.spacing(2),
		textAlign: "center",
		color: theme.palette.text.secondary,
		fontSize: "1rem",
	},
	actionButton: {
		marginTop: theme.spacing(2),
	},
}));

const logo = `${window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/logotipos/login.png`;
const randomValue = Math.random();

const logoWithRandom = `${logo}?r=${randomValue}`;

const TicketsCustom = () => {
	const classes = useStyles();
	const { ticketId } = useParams();
	const [listWidthPx, setListWidthPx] = useState(readInitialWidth);

	useEffect(() => {
		const onResize = () => setListWidthPx((w) => clampListWidth(w));
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const beginResize = useCallback(
		(startClientX) => {
			const startW = listWidthPx;
			let lastW = startW;

			const onMove = (ev) => {
				const x = ev.clientX != null ? ev.clientX : ev.touches && ev.touches[0] && ev.touches[0].clientX;
				if (x == null) return;
				if (ev.cancelable && ev.type === "touchmove") ev.preventDefault();
				lastW = clampListWidth(startW + (x - startClientX));
				setListWidthPx(lastW);
			};

			const onEnd = () => {
				document.removeEventListener("mousemove", onMove);
				document.removeEventListener("mouseup", onEnd);
				document.removeEventListener("touchmove", onMove);
				document.removeEventListener("touchend", onEnd);
				document.removeEventListener("touchcancel", onEnd);
				document.body.style.cursor = "";
				document.body.style.userSelect = "";
				try {
					localStorage.setItem(STORAGE_KEY, String(lastW));
				} catch {
					/* noop */
				}
			};

			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
			document.addEventListener("mousemove", onMove);
			document.addEventListener("mouseup", onEnd);
			document.addEventListener("touchmove", onMove, { passive: false });
			document.addEventListener("touchend", onEnd);
			document.addEventListener("touchcancel", onEnd);
		},
		[listWidthPx]
	);

	return (
		<div className={classes.chatContainer}>
			<div className={classes.chatPapper}>
				<div className={classes.splitRoot}>
					<div className={classes.listPane} style={{ width: listWidthPx }}>
						<TicketsManager />
					</div>
					<div
						className={classes.resizer}
						role="separator"
						aria-orientation="vertical"
						aria-label={i18n.t("tickets.aria.resizeTicketsPanel")}
						tabIndex={0}
						onMouseDown={(e) => {
							e.preventDefault();
							beginResize(e.clientX);
						}}
						onTouchStart={(e) => {
							if (e.touches.length !== 1) return;
							e.preventDefault();
							beginResize(e.touches[0].clientX);
						}}
						onKeyDown={(e) => {
							const step = e.shiftKey ? 24 : 8;
							if (e.key === "ArrowLeft") {
								e.preventDefault();
								setListWidthPx((w) => {
									const n = clampListWidth(w - step);
									try {
										localStorage.setItem(STORAGE_KEY, String(n));
									} catch {
										/* noop */
									}
									return n;
								});
							}
							if (e.key === "ArrowRight") {
								e.preventDefault();
								setListWidthPx((w) => {
									const n = clampListWidth(w + step);
									try {
										localStorage.setItem(STORAGE_KEY, String(n));
									} catch {
										/* noop */
									}
									return n;
								});
							}
						}}
					/>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<Ticket />
						) : (
							<Paper square variant="outlined" className={classes.welcomeMsg}>
								<div className={classes.logoContainer}>
									<img
										className={classes.logoImage}
										src={logoWithRandom}
										alt={`${
											typeof process !== "undefined" && process.env
												? process.env.REACT_APP_NAME_SYSTEM
												: "Sistema"
										}`}
									/>
								</div>

								<Typography
									variant="h6"
									color="textPrimary"
									className={classes.welcomeText}
									style={{ fontWeight: "bold" }}
								>
									{i18n.t("tickets.welcome.title")}
								</Typography>

								<Typography variant="body2" color="textSecondary" className={classes.subtitleText}>
									{i18n.t("tickets.welcome.subtitle")}
								</Typography>
							</Paper>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default TicketsCustom;
